import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormValuesType, useComposableForm } from 'src/components/CustomFields/ComposableForm';
import { CRMAccount, CRMAddress } from 'src/classes/CRM/CRMIndexedDBTypes';
import { Singleton as IndexDbCrm } from 'src/classes/CRM/CRMIndexedDB';
import { CustomFieldValueDefinition } from '@alucio/aws-beacon-amplify/src/models';
import isEquals from 'lodash/isEqual'
import { ObjectWithId } from 'src/components/CustomFields/ComposableFormUtilities';
import get from 'lodash/get';
import { MEDICAL_INQUIRY_SUPPORTED_METHODS } from 'src/classes/CRM/Translators/VeevaVault/VeevaVaultControlHandler';
import { useUserTenant } from 'src/state/redux/selector/user';
import { getFormatedName } from 'src/screens/Profile/CRMIntegration';

const ACCOUNT_PROPERTY_NAME = 'account__v';
const DELIVERY_METHOD_PROPERTY_NAME = 'delivery_method__v';

const getAddress = (address: CRMAddress, addressFormat?: string) => {
  return addressFormat ? getFormatedName(address, addressFormat) : address.name__v;
};

const formatPhoneNumber = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  const ten = digits.slice(-10);
  if (ten.length !== 10) return phone;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
};

const getPhones = (account: CRMAccount): string[] => {
  const phones: string[] = [
    account.office_phone_cda__v as string,
    account.mobile_phone_cda__v as string,
    ...account.addresses?.reduce<string[]>((acc, { phone_cda__v }) => phone_cda__v ? [...acc, phone_cda__v] : acc, []),
  ];

  return [...new Set(phones.filter(Boolean).map(formatPhoneNumber))];
};

type SUPPORTED_METHODS = 'phone__v' | 'mail__v' | 'fax__v' | 'urgent_mail__v' | 'email__v';

export const getUpdate = (account: CRMAccount, addressFormat?: string) : {
  [key in SUPPORTED_METHODS]: () => {
    updates: CustomFieldValueDefinition[];
    inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS
  };
} => ({
  'mail__v': () => {
    const addresses = account.addresses.map<CustomFieldValueDefinition>((address) => {
      const formattedAddress = getAddress(address, addressFormat);
      return {
        id: address.id,
        value: formattedAddress,
        createdAt: new Date().toISOString(),
        dependentValueIds: ['mail__v'],
      };
    });
    return {
      updates: addresses,
      inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS.MAIL,
    }
  },
  'phone__v': () => {
    const phones = getPhones(account);
    if (!phones.length) return { updates: [], inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS.PHONE };
    const newPhones: CustomFieldValueDefinition[] =
      phones.map((phone) => ({
        id: phone,
        value: phone,
        label: phone,
        createdAt: new Date().toISOString(),
        dependentValueIds: ['phone__v'],
      }));
    return {
      updates: newPhones,
      inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS.PHONE,
    }
  },
  'email__v': () => {
    const email = account.PersonEmail as string;
    if (!email) return { updates: [], inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS.EMAIL };
    const newEmail : CustomFieldValueDefinition[] = [
      {
        id: email,
        value: email,
        label: email,
        createdAt: new Date().toISOString(),
        dependentValueIds: ['email__v'],
      },
    ]
    return {
      updates: newEmail,
      inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS.EMAIL,
    }
  },
  'urgent_mail__v': () => {
    const addresses = account.addresses.map<CustomFieldValueDefinition>((address) => {
      const formattedAddress = getAddress(address, addressFormat);
      return {
        id: address.id,
        value: formattedAddress,
        createdAt: new Date().toISOString(),
        dependentValueIds: ['urgent_mail__v'],
      };
    });
    return {
      updates: addresses,
      inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS.MAIL,
    }
  },
  'fax__v': () => {
    const faxVod = account.fax_cda__v as string;
    if (!faxVod) return { updates: [], inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS.FAX };
    const newFaxVod : CustomFieldValueDefinition[] = [
      {
        id: faxVod,
        value: faxVod,
        label: faxVod,
        createdAt: new Date().toISOString(),
        dependentValueIds: ['fax__v'],
      },
    ]
    return {
      updates: newFaxVod,
      inquiry: MEDICAL_INQUIRY_SUPPORTED_METHODS.FAX,
    }
  },
})

type InquiryWatchParams = {
  account__v: string | string[] | ObjectWithId[];
  delivery_method__v: string | string[] | ObjectWithId[];
}

export const useVeevaVaultInquiryWatch = (
  rhForm: UseFormReturn<FormValuesType>,
): void => {
  const { watch, getValues } = rhForm;
  const watchMedicalInquiry = watch([ACCOUNT_PROPERTY_NAME, DELIVERY_METHOD_PROPERTY_NAME]);
  const { updateCustomFieldValues } = useComposableForm();
  const lastElementSelected = useRef<null | InquiryWatchParams>(null);
  const tenant = useUserTenant();
  const addressFormat = tenant?.config.crmIntegration?.accountsSettings?.addressSettings?.displaySettings?.shortened;

  const updateCustomFieldValuesBasedOnMethod = (
    method: SUPPORTED_METHODS,
    account: CRMAccount,
  ) => {
    const result = getUpdate(account, addressFormat)?.[method]?.();
    if (!result) return;
    const { updates, inquiry } = result;
    updateCustomFieldValues(updates, inquiry);
  };

  useEffect(() => {
    const doAction = async () => {
      if (!watchMedicalInquiry) return;
      const inquiry = getValues() as InquiryWatchParams;

      // if inquiry has no value
      if (!inquiry) return;

      // check if lastElementSelected.current is different from inquiry
      if (isEquals(lastElementSelected.current, inquiry)) return;

      const selectedAccountId = get(inquiry, ACCOUNT_PROPERTY_NAME, '') as string;
      const selectedDeliveryMethod = get(inquiry, DELIVERY_METHOD_PROPERTY_NAME, '') as SUPPORTED_METHODS;

      // check is the delivery method has a value
      if (!selectedAccountId || !selectedDeliveryMethod) return;

      // update lastElementSelected after all the checks
      lastElementSelected.current = inquiry;

      const account = await IndexDbCrm.getById<CRMAccount>('ACCOUNT', selectedAccountId);
      // create custom field values based on the delivery method
      await updateCustomFieldValuesBasedOnMethod(selectedDeliveryMethod, account);
    }

    doAction();
  }, [watchMedicalInquiry]);
};
